import { getCookie } from 'cookies-next';
import { trackEvent } from 'integrations/metrics';
import { EventNames } from 'integrations/metrics/events';
import { EventAction } from 'integrations/metrics/types';
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import {
  getAllProfessionals,
  getSelectableProfessionals,
  getSelectedOrPotentialServicesProviders,
} from 'selectors/professionals';
import {
  getMapServiceToSelectedOption,
  getSelectedServices,
} from 'selectors/services';

import { getServiceTokensFromGuids } from 'utils/serviceSelectionUtils';

import { API_VERSIONS } from '../constants';
import {
  trackBookFail,
  trackBookForSola,
  trackBookSuccess,
} from '../utils/googleAnalytics';
import { cleanPhone } from '../utils/helpers';
import * as types from './ActionTypes';
import api from './ApiActions';

// Helper to decide which resource-management flow to use
const isResourceManagementEnabled = () => {
  if (typeof window !== 'undefined') {
    return Boolean(window.__RESOURCE_MANAGEMENT_ENABLED__);
  }

  return false;
};

const GENIUS_AI_SOURCE_KEY = 'geniusAiSource';

function submitBookingSuccess(data, navigateToNextStep) {
  return (dispatch, getState) => {
    const state = getState();
    const professionals = getAllProfessionals(state);
    const professional = professionals.find(
      (p) => p.guid === data.provider_guid,
    );
    const selectedServices = getSelectedServices(state);
    const selectionMap = getMapServiceToSelectedOption(state);
    const optionCount = selectedServices.reduce((acc, current) => {
      if (selectionMap[current]) {
        return acc + 1;
      }
      return acc;
    }, 0);

    dispatch({
      type: types.SUBMIT_BOOKING_SUCCESS,
      payload: professional ? { ...data, professional } : data,
    });

    dispatch({
      type: types.CLEAR_PROFESSIONAL,
    });

    try {
      const geniusAiSource = getCookie(GENIUS_AI_SOURCE_KEY) === 'true';

      trackEvent(EventNames.BOOKING_SUCCESS, {
        action: EventAction.SUCCESS,
        apppointment_guid: [data.guid],
        bundle_size: 1,
        bundle_token: null,
        is_msmp_booking: false,
        num_services: data.services.length,
        service_option_count: optionCount,
        geniusAiSource: geniusAiSource,
        resource_ids: data.scheduled_resources ?? [],
      });
      if (window.FB) {
        window.FB.AppEvents.logEvent('CLIENT_BOOKED_APPOINTMENT');
      }

      if (window.fbq) {
        window.fbq('track', 'Schedule');
      }

      trackBookForSola();
      if (data && data.guid) {
        trackBookSuccess(data.guid);
      }
    } catch (e) {
      /* NOOP */
    }

    navigateToNextStep({
      stepKey: 'success',
    });
  };
}

function submitBookingFailure(data) {
  return (dispatch) => {
    dispatch({
      type: types.SUBMIT_BOOKING_FAILURE,
      payload: data,
    });

    trackBookFail();
  };
}

function submitRescheduleSuccess(data, navigateToNextStep) {
  return (dispatch, getState) => {
    const state = getState();
    const professionals = getAllProfessionals(state);
    const professional = professionals.find(
      (p) => p.guid === data.provider_guid,
    );

    dispatch({
      type: types.SUBMIT_BOOKING_RESCHEDULE_SUCCESS,
      payload: professional ? { ...data, professional } : data,
    });

    dispatch({
      type: types.CLEAR_PROFESSIONAL,
    });

    navigateToNextStep({
      stepKey: 'success',
      action: 'reschedule',
    });
  };
}

function submitRescheduleFailure(data, navigateToNextStep) {
  return (dispatch) => {
    dispatch({
      type: types.SUBMIT_BOOKING_RESCHEDULE_FAILURE,
      payload: data,
    });

    trackBookFail();

    navigateToNextStep({
      stepKey: 'error',
      action: 'reschedule',
    });
  };
}

export function rescheduleAppointment(navigateToNextStep) {
  return (dispatch, getState) => {
    const state = getState();
    const { appointment, publicUser } = state;

    const { appointmentGuid, time, appointmentToken, professional } =
      appointment;

    const serviceTokens = getServiceTokensFromGuids(
      professional?.services,
      appointment.service_guids,
    );

    const businessToken = publicUser.business_token;

    dispatch(
      api.post(`/web/appointments/${appointmentGuid}/reschedule`, {
        version: API_VERSIONS.v3,
        data: {
          start_time: time,
          appointment_token: appointmentToken,
          service_tokens: serviceTokens,
          ...(businessToken && { business_token: businessToken }),
        },
        params: {
          slug: publicUser.slug,
        },
        request: types.SUBMIT_BOOKING_RESCHEDULE_REQUEST,
        success: (data) => submitRescheduleSuccess(data, navigateToNextStep),
        failure: (data) => submitRescheduleFailure(data, navigateToNextStep),
      }),
    );
  };
}

function bookAppointment(navigateToNextStep) {
  return (dispatch, getState) => {
    const state = getState();
    const { appointment, publicUser, bookableAppointments } = state;

    const selectableProfessionals = getSelectableProfessionals.resultFunc(
      getAllProfessionals(state),
      appointment.service_guids.map((guid) => ({ guid })),
    );

    const services = bookableAppointments?.serviceProviders.reduce(
      (acc, serviceProvider) => {
        return [...acc, ...serviceProvider.services];
      },
      [],
    );

    const serviceTokens = getServiceTokensFromGuids(
      services,
      appointment.service_guids,
    );

    const providerBusinessMemberToken =
      appointment.professional?.business_member_token;
    const businessToken = publicUser.business_token;

    dispatch(
      api.post('/appointments', {
        data: {
          services_guids: appointment.service_guids,
          ...(appointment.professional
            ? { user_guid: appointment.professional.guid }
            : { users_guids: selectableProfessionals.map((p) => p.guid) }),
          start_time: appointment.time,
          user_slug: publicUser.slug,
          client_name: `${appointment.firstName} ${appointment.lastName}`,
          client_email: appointment.email,
          client_phone: cleanPhone(appointment.phone).substring(1),
          pronouns: appointment.pronouns,
          address_1: appointment.addressLine1,
          address_2: appointment.addressLine2,
          city: appointment.city,
          state: appointment.state,
          postal_code: appointment.zipCode,
          stripe_token: appointment.stripe_token || '',
          rwg_token: appointment.rwg_token,
          waitlist_guid: getCookie('WAITLIST_GUID'),
          cancellation_policy_acknowledged:
            appointment.cancellationPolicyAcknowledged,
          ...(appointment.agreedToSmsConsent && {
            agreed_to_sms_consent: true,
          }), // only send this property if has accepted consent. No such thing as 'declining consent' while booking, that is done only through SMS later from the backend
          geniusAiSource: getCookie(GENIUS_AI_SOURCE_KEY) === 'true',
          service_tokens: serviceTokens,
          provider_business_member_token: providerBusinessMemberToken,
          ...(businessToken && { business_token: businessToken }),
        },
        request: types.SUBMIT_BOOKING_REQUEST,
        success: (data) => submitBookingSuccess(data, navigateToNextStep),
        failure: (data) => submitBookingFailure(data),
      }),
    );
  };
}

function getPaymentSettingForClientSuccess(data, navigateToNextStep) {
  return (dispatch) => {
    dispatch({
      type: types.GET_PAYMENT_SETTING_FOR_CLIENT_SUCCESS,
      payload: data,
    });

    trackEvent(EventNames.PAYMENT_SETTING_FOR_CLIENT_SUCCESS, {
      action: EventAction.SUCCESS,
    });

    if (data.require_card || data.require_deposit) {
      navigateToNextStep({ stepKey: 'payment' });
    } else {
      dispatch(bookAppointment(navigateToNextStep));
    }
  };
}

function getPaymentSettingForClientFailure(data, navigateToNextStep) {
  return (dispatch) => {
    dispatch({
      type: types.GET_PAYMENT_SETTING_FOR_CLIENT_FAILURE,
      payload: data,
    });

    navigateToNextStep({
      stepKey: 'error',
    });
  };
}

export function contactStylist(contactData, slug) {
  return api.post(`/web/${slug}/contact`, {
    version: API_VERSIONS.v3,
    data: contactData,
    request: types.CONTACT_STYLIST_REQUEST,
    success: types.CONTACT_STYLIST_SUCCESS,
    failure: types.CONTACT_STYLIST_FAILURE,
  });
}

export function resetContactForm() {
  return (dispatch) => {
    dispatch({
      type: types.CONTACT_FORM_RESET,
    });
  };
}

export function getAvailableDates({
  slug,
  date,
  service_guids,
  provider_guids,
  appointment_guid = undefined,
  appointment_token = undefined,
}) {
  return (dispatch, getState) => {
    const state = getState();
    const servicesProviders = getSelectedOrPotentialServicesProviders(state);
    const providerGuids = !isEmpty(provider_guids)
      ? provider_guids
      : servicesProviders.map((p) => p.guid);

    const allProfessionals = getAllProfessionals(state);
    const providerBusinessMemberTokens = providerGuids
      .map((guid) => {
        const professional = allProfessionals.find((p) => p.guid === guid);

        return professional?.business_member_token;
      })
      .filter(Boolean);
    const businessToken = state.publicUser.business_token;

    const services = state.bookableAppointments?.serviceProviders.reduce(
      (acc, serviceProvider) => {
        return [...acc, ...serviceProvider.services];
      },
      [],
    );

    const serviceTokens = getServiceTokensFromGuids(services, service_guids);

    const commonParams = {
      month: moment(date).format('MMMM'),
      year: moment(date).format('YYYY'),
      slug,
      service_guids,
      provider_guids: providerGuids,
      appointment_guid,
    };

    const usePost = isResourceManagementEnabled();

    if (usePost) {
      return api.post('/web/available_times', {
        version: API_VERSIONS.v3,
        data: {
          ...commonParams,
          ...(businessToken && { business_token: businessToken }),
          provider_business_member_tokens: providerBusinessMemberTokens,
          ...(appointment_token && { appointment_token }),
          ...(serviceTokens.length > 0 && { service_tokens: serviceTokens }),
        },
        request: types.GET_AVAILABLE_DATES_REQUEST,
        success: types.GET_AVAILABLE_DATES_SUCCESS,
        failure: types.GET_AVAILABLE_DATES_FAILURE,
      })(dispatch, getState);
    }

    return api.get('/web/available_times', {
      version: API_VERSIONS.v3,
      params: commonParams,
      request: types.GET_AVAILABLE_DATES_REQUEST,
      success: types.GET_AVAILABLE_DATES_SUCCESS,
      failure: types.GET_AVAILABLE_DATES_FAILURE,
    })(dispatch, getState);
  };
}

export function lookUpClientPaymentSetting(
  { slug, firstName, lastName, email, phone },
  navigateToNextStep,
) {
  return (dispatch, getState) => {
    const {
      appointment: {
        service_guids: services_guids = [],
        time: start_time,
      } = {},
    } = getState();

    const name = `${firstName} ${lastName}`;

    dispatch(
      api.post('/web/bookings/payment_requirements', {
        version: API_VERSIONS.v3,
        data: { slug, email, phone, name, services_guids, start_time },
        request: types.GET_PAYMENT_SETTING_FOR_CLIENT_REQUEST,
        success: (data) =>
          getPaymentSettingForClientSuccess(data, navigateToNextStep),
        failure: (data) =>
          getPaymentSettingForClientFailure(data, navigateToNextStep),
      }),
    );
  };
}

export function createCardToken(
  { stripe, cardElement, name, submitBookingOnSuccess },
  navigateToNextStep,
) {
  return async (dispatch) => {
    dispatch({
      type: types.GET_CARD_TOKEN_REQUEST,
    });

    try {
      const { token, error } = await stripe.createToken(cardElement, {
        name,
      });

      if (error) {
        dispatch({
          type: types.GET_CARD_TOKEN_FAILURE,
          payload: error?.message,
        });
      } else {
        dispatch({
          type: types.GET_CARD_TOKEN_SUCCESS,
          payload: token.id,
        });

        trackEvent(EventNames.SUBMIT_PAYMENT_INFO_SUCCESS, {
          action: EventAction.SUCCESS,
        });

        if (submitBookingOnSuccess) {
          dispatch(bookAppointment(navigateToNextStep));
        }
      }
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
    }
  };
}
